import type { PartyFeature, PartyPlan } from "./types/parties-api";

/**
 * Party plan entitlements.
 *
 * Free parties are capped at a small group. Full standings are included on
 * every plan: hiding ranks 4+ is not a meaningful perk, especially after the
 * show ends. A one-time Party Pass unlocks unlimited guests, chat, a custom
 * voting deadline, recap, and live reactions for that party only. Additional
 * plans (e.g. corporate
 * branding) can be added later without changing feature-check call sites.
 *
 * The free-party size is stored in app settings (not this constant). This
 * default is only the seed/fallback until an admin changes it.
 */

export const PARTY_PLANS = ["free", "party_pass"] as const;
export const DEFAULT_FREE_PARTY_MEMBER_CAP = 5;
export const FREE_LEADERBOARD_RANK_LIMIT = 3;
export const PARTY_PASS_DISPLAY_NAME = "Party Pass";

export type PartyPassMarketedFeatureId =
  | "unlimitedGuests"
  | "customVotingDeadline"
  | Exclude<PartyFeature, "branding" | "expandedLeaderboard">;

export const PARTY_PASS_FEATURES: ReadonlyArray<{
  id: PartyPassMarketedFeatureId;
  label: string;
  description: string;
}> = [
  {
    id: "unlimitedGuests",
    label: "Unlimited guests",
    description: "This party has no guest cap",
  },
  {
    id: "chat",
    label: "Party chat",
    description: "Chat with everyone in this party",
  },
  {
    id: "recap",
    label: "End-of-show recap",
    description: "A shareable story of the final standings",
  },
  {
    id: "liveReactions",
    label: "Live winner reactions",
    description: "React as winners are announced",
  },
  {
    id: "customVotingDeadline",
    label: "Custom voting deadline",
    description: "Lock picks when you want, up to when the show starts",
  },
] as const;

export function getPartyPassFeatures(): ReadonlyArray<{
  id: PartyPassMarketedFeatureId;
  label: string;
  description: string;
}> {
  return PARTY_PASS_FEATURES;
}

export function getPartyPassCallToAction(): string {
  return "A one-time payment unlocks unlimited guests, chat, a custom voting deadline, recap, and live reactions for this party only.";
}

export function getPartyPassSummary(freeMemberCap: number): string {
  return `This free party includes up to ${freeMemberCap} people. ${getPartyPassCallToAction()}`;
}

export function getPartyPassCollapsedTeaser(freeMemberCap: number): string {
  return `This party is limited to ${freeMemberCap} guests.`;
}

export function resolveGuestCapForCopy(input: {
  memberCap?: number | null;
  memberCapOverride?: number | null;
  globalFreeMemberCap: number;
}): number {
  if (isPositiveMemberCap(input.memberCap)) {
    return input.memberCap;
  }

  if (isPositiveMemberCap(input.memberCapOverride)) {
    return input.memberCapOverride;
  }

  return input.globalFreeMemberCap;
}

function isPositiveMemberCap(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

export function getPartyPassUnlockedSummary(): string {
  return "Unlimited guests, chat, a custom voting deadline, recap, and live reactions are included for this party.";
}

export interface PartyEntitlements {
  plan: PartyPlan;
  memberCap: number | null;
  features: Record<PartyFeature, boolean>;
}

const PLAN_FEATURES: Record<PartyPlan, Record<PartyFeature, boolean>> = {
  free: {
    branding: false,
    chat: false,
    expandedLeaderboard: true,
    recap: false,
    liveReactions: false,
  },
  party_pass: {
    branding: true,
    chat: true,
    expandedLeaderboard: true,
    recap: true,
    liveReactions: true,
  },
};

export interface ResolvePartyMemberCapInput {
  plan?: PartyPlan | string | null;
  memberCapOverride?: number | null;
  globalFreeMemberCap: number;
}

export function isPartyPlan(value: unknown): value is PartyPlan {
  return value === "free" || value === "party_pass";
}

export function normalizePartyPlan(value: unknown): PartyPlan {
  return isPartyPlan(value) ? value : "free";
}

export function resolvePartyMemberCap(input: ResolvePartyMemberCapInput): number | null {
  if (normalizePartyPlan(input.plan) === "party_pass") {
    return null;
  }

  if (isPositiveMemberCap(input.memberCapOverride)) {
    return input.memberCapOverride;
  }

  return input.globalFreeMemberCap;
}

export function getPartyEntitlements(
  plan: PartyPlan | string | null | undefined,
  options: {
    globalFreeMemberCap?: number;
    memberCapOverride?: number | null;
  } = {},
): PartyEntitlements {
  const normalized = normalizePartyPlan(plan);
  return {
    plan: normalized,
    memberCap: resolvePartyMemberCap({
      plan: normalized,
      memberCapOverride: options.memberCapOverride,
      globalFreeMemberCap:
        options.globalFreeMemberCap ?? DEFAULT_FREE_PARTY_MEMBER_CAP,
    }),
    features: PLAN_FEATURES[normalized],
  };
}

export function isPaidParty(
  plan: PartyPlan | string | null | undefined,
): boolean {
  return normalizePartyPlan(plan) === "party_pass";
}

export function getPartyMemberCap(
  plan: PartyPlan | string | null | undefined,
  options: {
    globalFreeMemberCap?: number;
    memberCapOverride?: number | null;
  } = {},
): number | null {
  return getPartyEntitlements(plan, options).memberCap;
}

export function canAddPartyMember(
  memberCap: number | null,
  currentMemberCount: number,
): boolean {
  if (!Number.isFinite(currentMemberCount) || currentMemberCount < 0) {
    return false;
  }

  return memberCap === null || currentMemberCount < memberCap;
}

export function remainingPartyMemberSlots(
  memberCap: number | null,
  currentMemberCount: number,
): number | null {
  if (memberCap === null) {
    return null;
  }

  return Math.max(memberCap - Math.max(currentMemberCount, 0), 0);
}

/** Show a prominent guest-cap warning when this many or fewer free spots remain. */
export const PARTY_MEMBER_CAP_WARNING_REMAINING = 2;

export function isPartyMemberCapNearLimit(
  memberCap: number | null,
  currentMemberCount: number,
): boolean {
  const remaining = remainingPartyMemberSlots(memberCap, currentMemberCount);
  if (remaining === null) {
    return false;
  }

  return remaining <= PARTY_MEMBER_CAP_WARNING_REMAINING;
}

export function hasPartyFeature(
  plan: PartyPlan | string | null | undefined,
  feature: PartyFeature,
): boolean {
  return getPartyEntitlements(plan).features[feature];
}

export function isPodiumRank(rank: number): boolean {
  return rank >= 1 && rank <= FREE_LEADERBOARD_RANK_LIMIT;
}

/** Full standings are included on every current plan. */
export function shouldRevealLeaderboardRank(
  plan: PartyPlan | string | null | undefined,
  rank: number,
  isCurrentUser: boolean,
): boolean {
  if (hasPartyFeature(plan, "expandedLeaderboard") || isCurrentUser) {
    return true;
  }

  return rank <= FREE_LEADERBOARD_RANK_LIMIT;
}

export function filterLeaderboardForViewer<
  T extends { rank: number; userId: string },
>(
  plan: PartyPlan | string | null | undefined,
  entries: readonly T[],
  currentUserId: string,
): T[] {
  return entries.filter((entry) =>
    shouldRevealLeaderboardRank(
      plan,
      entry.rank,
      entry.userId === currentUserId,
    ),
  );
}

export function leaderboardIsTruncated<
  T extends { rank: number; userId: string },
>(
  plan: PartyPlan | string | null | undefined,
  entries: readonly T[],
  currentUserId: string,
): boolean {
  return (
    filterLeaderboardForViewer(plan, entries, currentUserId).length <
    entries.length
  );
}

export type PartyRecapAccess = "allowed" | "grandfathered" | "upgrade_required";

export function getPartyRecapAccess(
  plan: PartyPlan | string | null | undefined,
  hasCompletedRecap: boolean,
): PartyRecapAccess {
  if (hasPartyFeature(plan, "recap")) {
    return "allowed";
  }

  return hasCompletedRecap ? "grandfathered" : "upgrade_required";
}

/**
 * Recaps are generated only for parties that include party chat.
 * Chat and the recap feature both ship with Party Pass today. Chat is
 * required so a future plan cannot grow a recap without party chat.
 */
export function shouldGeneratePartyRecap(
  plan: PartyPlan | string | null | undefined,
): boolean {
  return hasPartyFeature(plan, "chat") && hasPartyFeature(plan, "recap");
}

export function resolvePartyRecapStatus(
  plan: PartyPlan | string | null | undefined,
  recap: {
    status: "pending" | "generating" | "completed" | "failed";
    retryCount: number;
    payload: unknown;
  } | null,
): {
  status: "pending" | "generating" | "completed" | "failed" | "not_ready" | "upgrade_required";
  retryCount: number;
} {
  const hasCompletedRecap =
    recap?.status === "completed" && Boolean(recap.payload);
  const access = getPartyRecapAccess(plan, hasCompletedRecap);

  if (access === "upgrade_required") {
    return { status: "upgrade_required", retryCount: 0 };
  }

  if (!recap) {
    return { status: "not_ready", retryCount: 0 };
  }

  return { status: recap.status, retryCount: recap.retryCount };
}

export function partyLiveReactionsLockedMessage(): string {
  return `Live winner reactions are included with ${PARTY_PASS_DISPLAY_NAME}.`;
}

export const PARTY_MEMBER_CAP_ERROR_CODE = "PARTY_MEMBER_CAP_REACHED" as const;

export function partyMemberCapMessage(
  audience: "host" | "guest" = "guest",
  memberCap: number = DEFAULT_FREE_PARTY_MEMBER_CAP,
): string {
  if (audience === "host") {
    return `This free party is full (${memberCap} people). Unlock ${PARTY_PASS_DISPLAY_NAME} to invite more friends.`;
  }

  return `This free party is full (${memberCap} people). Ask the host to unlock ${PARTY_PASS_DISPLAY_NAME} to add more friends.`;
}

export class PartyMemberCapError extends Error {
  readonly code = PARTY_MEMBER_CAP_ERROR_CODE;

  constructor(message: string = partyMemberCapMessage("guest")) {
    super(message);
    this.name = "PartyMemberCapError";
  }
}

export function isPartyMemberCapError(
  error: unknown,
): error is PartyMemberCapError {
  if (error instanceof PartyMemberCapError) {
    return true;
  }

  if (!error || typeof error !== "object") {
    return false;
  }

  const candidate = error as { name?: unknown; code?: unknown };
  return (
    candidate.name === "PartyMemberCapError" ||
    candidate.code === PARTY_MEMBER_CAP_ERROR_CODE
  );
}
